import { Col, Row } from 'antd';
import styles from './scale.module.css';
import Totals from './_Totals';
import { Question } from '../QuestionsList';
import { Colors } from '../../../core/Theme';
import { Bar } from '@ant-design/charts';
import { memo, useRef, useEffect } from 'react';
import { formatNumber, formatPercentage } from '../../../../components/Percent';
import useWindowSize from '../../../../components/useWindowResize';

const Scale = ({ data, question }: { data: any; question: Question }) => {
    const satisfactionIndex =
        (question.extended_props || {}).average_method === 'satisfaction_index';
    const numbers = Array.from(
        new Array((question.max_value || 10) - (question.min_value || 0) + 1),
        (x, i) => i + (question.min_value || 0),
    );
    const maxPerc = Math.max(
        ...Object.keys(data.aggregated).map((k) =>
            Number(
                (data.aggregated[k] || { total: 0, percentage: 0 }).percentage,
            ),
        ),
    );

    let chart1Ref = useRef<any>(null);
    let chart1ContainerRef = useRef<any>(null);

    const size = useWindowSize();

    useEffect(() => {
        if (chart1Ref && chart1Ref.current) {
            const currentWidth =
                chart1ContainerRef?.current?.offsetWidth || undefined;
            chart1Ref.current
                .getChart()
                .changeSize(
                    currentWidth - 24,
                    (data.consolidated.length / 3) * 27 + 30,
                );
        }
    }, [size, data]);

    return (
        <div className={styles.container}>
            <Row>
                <Col md={24} lg={!!satisfactionIndex ? 12 : 24}>
                    <div className={styles.scaleChart}>
                        {numbers
                            .map((k) => ({
                                ...(data.aggregated[k] || {
                                    total: 0,
                                    percentage: 0,
                                }),
                                id: k,
                            }))
                            .sort((a, b) => a.id - b.id)
                            .map((k) => (
                                <div key={k.id} className={styles.chart}>
                                    <div className={styles.chartTitle}>
                                        {formatPercentage(k.percentage, 2)}
                                    </div>
                                    <div
                                        className={styles.chartBar}
                                        style={{
                                            height:
                                                k.percentage === 0
                                                    ? '7px'
                                                    : undefined,
                                            flex: k.percentage / maxPerc,
                                            backgroundColor:
                                                k.id < 6
                                                    ? Colors.charts.gray
                                                    : k.id > 7
                                                      ? Colors.charts.green
                                                      : Colors.charts.greenish,
                                        }}
                                    >
                                        &nbsp;
                                    </div>
                                    <div
                                        className={styles.chartHead}
                                        style={{
                                            backgroundColor:
                                                k.id < 6
                                                    ? Colors.charts.gray
                                                    : k.id > 7
                                                      ? Colors.charts.green
                                                      : Colors.charts.turquoise,
                                            borderColor:
                                                k.id < 6
                                                    ? Colors.charts.gray
                                                    : k.id > 7
                                                      ? Colors.charts.green
                                                      : Colors.charts.greenish,
                                        }}
                                    >
                                        <div
                                            style={{
                                                color:
                                                    k.id < 6
                                                        ? Colors.charts.gray
                                                        : k.id > 7
                                                          ? Colors.charts.green
                                                          : Colors.charts
                                                                .greenish,
                                            }}
                                        >
                                            {k.id}
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </div>
                </Col>
                {!!satisfactionIndex && (
                    <Col md={24} lg={12}>
                        <h2>Índice de satisfacción</h2>
                        <div
                            className={styles.chartScroll}
                            ref={chart1ContainerRef}
                        >
                            <Bar
                                ref={chart1Ref}
                                data={data.consolidated.sort(
                                    (a: any, b: any) => a.order - b.order,
                                )}
                                yField="text"
                                xField="average"
                                seriesField="type"
                                autoFit={false}
                                isPercent
                                isStack
                                legend={{ position: 'bottom' }}
                                height={
                                    (data.consolidated.length / 3) * 27 + 30
                                }
                                tooltip={{
                                    title: (title, datum) =>
                                        `Cant. respuestas: ${formatNumber(datum.total, 0)}`,
                                }}
                                xAxis={{ grid: null, label: null }}
                                yAxis={{
                                    grid: null,
                                    line: null,
                                    label: {
                                        autoEllipsis: true,
                                        formatter: (str) =>
                                            str.length > 30
                                                ? str.substring(0, 27) + '...'
                                                : str,
                                        style: {
                                            fontSize: 11,
                                            fontFamily:
                                                '"Montserrat", sans-serif',
                                            fill: Colors.black,
                                            fontWeight: 'normal',
                                        },
                                    },
                                }}
                                label={{
                                    style: {
                                        fill: Colors.black,
                                        fontFamily: '"Montserrat", sans-serif',
                                        fontWeight: 'bold',
                                        fontSize: 10,
                                        textAlign: 'center',
                                    },
                                    position: 'middle',
                                    content: (data) =>
                                        data.average > 0.2
                                            ? formatPercentage(data.average, 1)
                                            : '',
                                }}
                                color={[
                                    Colors.charts.green,
                                    Colors.charts.greenish,
                                    Colors.charts.gray,
                                ]}
                            />
                        </div>
                    </Col>
                )}
            </Row>

            <Totals data={data} showAverage={true} />
        </div>
    );
};

export default memo(Scale);
